import {
  clearTokens,
  getAccess,
  getBackendDeviceId,
  getRefresh,
  isAccessExpired,
  setAccess,
  setBackendDeviceId,
  setRefresh,
} from '@features/auth';

import { client as generatedClient } from './generated/client.gen';
import { refreshAuth as refreshApi } from './generated/sdk.gen';

let refreshPromise: Promise<boolean> | null = null;

/**
 * Подменяет существующий refresh новым через `/api/auth/refresh`.
 *
 * Защищает от параллельных вызовов: пока один refresh в полёте, остальные
 * ждут его результата. На неудачу — чистит токены и возвращает `false`.
 *
 * @returns `true` при успешном обновлении, `false` при отсутствии refresh или ошибке
 */
async function performRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  const stored = getRefresh();
  if (!stored) return false;

  const backendDeviceId = getBackendDeviceId();
  if (!backendDeviceId) return false;

  refreshPromise = (async (): Promise<boolean> => {
    try {
      const res = await refreshApi({
        body: { refreshToken: stored, deviceId: backendDeviceId },
      });
      const data = res.data;
      if (
        !data?.accessToken ||
        !data.refreshToken ||
        !data.deviceId ||
        data.accessExpiresInSeconds == null
      ) {
        clearTokens();
        return false;
      }
      setAccess(data.accessToken, data.accessExpiresInSeconds);
      setRefresh(data.refreshToken);
      setBackendDeviceId(data.deviceId);
      return true;
    } catch {
      clearTokens();
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Устанавливает axios-интерсепторы для авторизации и автоматического refresh.
 *
 * Должен быть вызван один раз при старте приложения (см. `main.tsx`).
 * Эндпоинты `/api/auth/otp/*` и `/api/auth/refresh` идут без заголовка
 * Authorization (для них токен не нужен или они сами выдают новый).
 */
export function setupHttpInterceptors(): void {
  const axiosInstance = generatedClient.instance;

  axiosInstance.interceptors.request.use(async (cfg) => {
    const url = cfg.url ?? '';
    const isPublic = url.includes('/api/auth/otp/') || url.includes('/api/auth/refresh');

    if (!isPublic) {
      if (isAccessExpired() && getRefresh()) {
        await performRefresh();
      }
      const token = getAccess();
      if (token) {
        cfg.headers.set('Authorization', `Bearer ${token}`);
      }
    }
    return cfg;
  });

  axiosInstance.interceptors.response.use(
    (res) => res,
    async (error: unknown) => {
      const err = error as {
        config?: {
          _retry?: boolean;
          url?: string;
          headers?: Record<string, string>;
        };
        response?: { status?: number };
      };
      const status = err.response?.status;
      const cfg = err.config;

      if (status === 401 && cfg && !cfg._retry && !cfg.url?.includes('/api/auth/')) {
        cfg._retry = true;
        const refreshed = await performRefresh();
        if (refreshed) {
          const token = getAccess();
          if (token) {
            return axiosInstance.request({
              ...cfg,
              headers: { ...(cfg.headers ?? {}), Authorization: `Bearer ${token}` },
            });
          }
        }
      }
      throw error;
    },
  );
}
